import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  CheckCircle2,
  Circle,
  Clock,
  Download,
  FileText,
  ShieldCheck,
  Users,
} from "lucide-react";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { chooseNegotiation, chooseSelfServe, useCase } from "@/lib/cases";

export const Route = createFileRoute("/case/$id")({
  head: ({ params }) => ({
    meta: [{ title: `ClaimGuard | Case ${params.id}` }],
  }),
  component: CasePage,
});

const reviewSteps = [
  "Reading invoice",
  "Extracting charges",
  "Researching state laws & regulations",
  "Comparing local rates",
  "Finding discrepancies",
  "Building negotiation strategy",
  "Creating your professional report",
];

const statusTrack = [
  { key: "received", label: "Received", body: "We've received your request." },
  { key: "reviewing", label: "Assigned", body: "Your case is assigned to a specialist." },
  { key: "negotiating", label: "Negotiating", body: "We're contacting the vendor on your behalf." },
  { key: "waiting_vendor", label: "In progress", body: "We're working to get the best outcome." },
  { key: "offer_received", label: "Offer / resolution", body: "We present the best offer to you." },
  { key: "completed", label: "Review Complete", body: "Your professional invoice review is complete." },
] as const;

function CasePage() {
  const { id } = useParams({ from: "/case/$id" });
  const c = useCase(id);

  if (!c) {
    return (
      <div className="min-h-dvh bg-background">
        <SiteHeader />
        <div className="container-page py-20 text-center">
          <div className="text-muted-foreground">Case not found.</div>
          <Link to="/dashboard" className="mt-4 inline-block text-trust">
            Back to my cases
          </Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const reviewing = c.status === "reviewing";
  const ready =
    c.status === "report_ready" ||
    c.status === "self_serve" ||
    c.status === "negotiating" ||
    c.status === "waiting_vendor" ||
    c.status === "offer_received" ||
    c.status === "completed";

  return (
    <div className="min-h-dvh bg-background">
      <SiteHeader />
      <div className="container-page py-10">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-navy"
        >
          <ArrowLeft className="h-4 w-4" /> My cases
        </Link>

        <div className="mt-4 flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-trust">
              Case file
            </div>
            <h1 className="mt-1 text-3xl text-navy md:text-4xl">{c.id}</h1>
            <div className="mt-1 text-sm text-muted-foreground">
              {c.details.vendor || "Vendor requires review"} · Submitted{" "}
              {new Date(c.createdAt).toLocaleString()}
              {c.fastTrack && (
                <span className="ml-2 rounded-full bg-fasttrack/20 px-2 py-0.5 text-[10px] font-bold uppercase text-fasttrack">
                  FastTrack
                </span>
              )}
            </div>
          </div>
          <StatusBadge status={c.status} />
        </div>

        {reviewing && (
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            <div className="rounded-2xl border border-border bg-card p-6 shadow-card md:col-span-2">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-trust">
                <Clock className="h-4 w-4" /> Review in progress
              </div>
              <h2 className="mt-2 font-display text-2xl text-navy">
                Our AI is analyzing your invoice
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                This usually takes 10–60 minutes. You'll be notified when it's ready.
              </p>
              <ul className="mt-5 space-y-2 text-sm">
                {reviewSteps.map((s, i) => (
                  <li key={s} className="flex items-center gap-2">
                    {i < 4 ? (
                      <CheckCircle2 className="h-4 w-4 text-savings" />
                    ) : (
                      <Circle className="h-4 w-4 animate-pulse text-muted-foreground" />
                    )}
                    <span className={i < 4 ? "text-foreground" : "text-muted-foreground"}>{s}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 h-2 overflow-hidden rounded-full bg-secondary">
                <div className="h-full w-2/3 animate-pulse rounded-full bg-trust" />
              </div>
            </div>
            <div className="rounded-2xl border border-border bg-navy p-6 text-navy-foreground shadow-card">
              <div className="text-xs font-semibold uppercase tracking-wider text-white/70">
                What to expect
              </div>
              <h3 className="mt-2 font-display text-xl">Your report will include</h3>
              <ul className="mt-4 space-y-2 text-sm text-white/80">
                {[
                  "25+ pages",
                  "Backed by law & market data",
                  "Clear savings opportunity",
                  "Ready-to-send negotiation letter",
                ].map((x) => (
                  <li key={x} className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-savings" /> {x}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {ready && (
          <div className="mt-8 grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <Link
                to="/report/$id"
                params={{ id: c.id }}
                className="group block overflow-hidden rounded-2xl border border-border bg-white shadow-card"
              >
                <div className="bg-navy p-6 text-navy-foreground">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-white/60">
                    <ShieldCheck className="h-3.5 w-3.5" /> Invoice Review Report
                  </div>
                  <div className="mt-2 font-display text-2xl">
                    Professional Findings & Negotiation Package
                  </div>
                  <div className="mt-1 text-xs text-white/60">File #: {c.id}</div>
                </div>
                <div className="flex items-center justify-between gap-4 p-6">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-savings-soft text-savings">
                      <FileText className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-semibold text-navy">View my report</div>
                      <div className="text-sm text-muted-foreground">
                        25+ pages · findings, laws, negotiation leverage
                      </div>
                    </div>
                  </div>
                  <span className="rounded-md bg-savings px-4 py-2 text-sm font-semibold text-savings-foreground group-hover:opacity-90">
                    Open report
                  </span>
                </div>
              </Link>

              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <button
                  onClick={() => chooseSelfServe(c.id)}
                  className="rounded-2xl border border-trust/20 bg-trust-soft p-6 text-left transition hover:shadow-card"
                >
                  <Download className="h-6 w-6 text-trust" />
                  <div className="mt-3 font-semibold text-navy">Download my report</div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    Review and negotiate on your own.
                  </div>
                </button>
                <button
                  onClick={() => chooseNegotiation(c.id)}
                  className="rounded-2xl border border-negotiate/20 bg-negotiate-soft p-6 text-left transition hover:shadow-card"
                >
                  <Users className="h-6 w-6 text-negotiate" />
                  <div className="mt-3 font-semibold text-navy">Have our experts negotiate</div>
                  <div className="mt-1 text-sm text-muted-foreground">
                    We handle it and keep you updated every step of the way.
                  </div>
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Case status
              </div>
              <ol className="mt-4 space-y-4">
                {statusTrack.map((s, i) => {
                  const activeIdx = statusTrack.findIndex((x) => x.key === c.status);
                  const done = i <= activeIdx;
                  const current = i === activeIdx;
                  return (
                    <li key={s.key} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div
                          className={`flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold ${
                            done
                              ? "bg-savings text-savings-foreground"
                              : "bg-secondary text-muted-foreground"
                          }`}
                        >
                          {done ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                        </div>
                        {i < statusTrack.length - 1 && (
                          <div className={`h-6 w-px ${done ? "bg-savings" : "bg-border"}`} />
                        )}
                      </div>
                      <div
                        className={
                          current ? "text-navy" : done ? "text-foreground" : "text-muted-foreground"
                        }
                      >
                        <div className="text-sm font-semibold">{s.label}</div>
                        <div className="text-xs">{s.body}</div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    received: { label: "Received", cls: "bg-secondary text-navy" },
    reviewing: { label: "AI reviewing", cls: "bg-trust-soft text-trust" },
    report_ready: { label: "Report ready", cls: "bg-savings-soft text-savings" },
    self_serve: { label: "Self-serve", cls: "bg-trust-soft text-trust" },
    negotiating: { label: "Negotiating", cls: "bg-negotiate-soft text-negotiate" },
    waiting_vendor: { label: "Waiting on vendor", cls: "bg-fasttrack-soft text-fasttrack" },
    offer_received: { label: "Offer received", cls: "bg-savings-soft text-savings" },
    completed: { label: "Review Complete", cls: "bg-savings-soft text-savings" },
  };
  const s = map[status] ?? map.received;
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${s.cls}`}
    >
      {s.label}
    </span>
  );
}
